import { useState } from "react";
import { useReboques } from "@/features/transporte/hooks/useTransporte";
import type { Reboque, SalvarReboqueRequest } from "@/features/transporte/types";
import { TIPOS_CARROCERIA } from "@/features/transporte/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Truck } from "lucide-react";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

const FORM_VAZIO: SalvarReboqueRequest = {
  placa: "",
  uf: "",
  renavam: "",
  capacidadeM3: undefined,
  capacidadeKg: undefined,
  taraKg: undefined,
  tipoCarroceria: "",
  ativo: true,
};

const UFS_BR = [
  "AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT",
  "PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO",
];

export default function ReboquePage() {
  const [busca, setBusca] = useState("");
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const { reboques, isLoading, criar, atualizar, excluir } = useReboques(
    mostrarInativos ? undefined : { ativo: true }
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState<Reboque | null>(null);
  const [inativarId, setInativarId] = useState<number | null>(null);
  const [form, setForm] = useState<SalvarReboqueRequest>(FORM_VAZIO);

  const filtrados = busca
    ? reboques.filter(
        (r) =>
          r.placa.toLowerCase().includes(busca.toLowerCase()) ||
          r.uf.toLowerCase().includes(busca.toLowerCase()) ||
          (r.tipoCarroceria ?? "").toLowerCase().includes(busca.toLowerCase())
      )
    : reboques;

  const paginacao = usePaginacaoLocal(filtrados);

  const abrirDialog = (reboque?: Reboque) => {
    if (reboque) {
      setEditando(reboque);
      setForm({
        placa: reboque.placa,
        uf: reboque.uf,
        renavam: reboque.renavam ?? "",
        capacidadeM3: reboque.capacidadeM3,
        capacidadeKg: reboque.capacidadeKg,
        taraKg: reboque.taraKg,
        tipoCarroceria: reboque.tipoCarroceria ?? "",
        ativo: reboque.ativo,
      });
    } else {
      setEditando(null);
      setForm(FORM_VAZIO);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editando) {
      await atualizar.mutateAsync({ id: editando.id, req: form });
    } else {
      await criar.mutateAsync(form);
    }
    setDialogOpen(false);
  };

  if (isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Reboques</h1>
          <p className="text-muted-foreground">Cadastro de reboques para MDFe</p>
        </div>
        <Button onClick={() => abrirDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Reboque
        </Button>
      </div>

      <div className="flex gap-4 items-center">
        <div className="max-w-sm flex-1">
          <Input
            placeholder="Buscar por placa, UF ou carroceria..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={mostrarInativos}
            onChange={(e) => setMostrarInativos(e.target.checked)}
          />
          Exibir inativos
        </label>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Placa</TableHead>
              <TableHead>UF</TableHead>
              <TableHead>RENAVAM</TableHead>
              <TableHead>Tipo Carroceria</TableHead>
              <TableHead>Tara (kg)</TableHead>
              <TableHead>Cap. (kg)</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium font-mono">{r.placa}</TableCell>
                <TableCell>{r.uf}</TableCell>
                <TableCell>{r.renavam ?? "-"}</TableCell>
                <TableCell className="capitalize">{r.tipoCarroceria ?? "-"}</TableCell>
                <TableCell>{r.taraKg != null ? r.taraKg.toLocaleString("pt-BR") : "-"}</TableCell>
                <TableCell>{r.capacidadeKg != null ? r.capacidadeKg.toLocaleString("pt-BR") : "-"}</TableCell>
                <TableCell>
                  <Badge variant={r.ativo ? "default" : "secondary"}>
                    {r.ativo ? "Ativo" : "Inativo"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => abrirDialog(r)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {r.ativo && (
                      <Button variant="ghost" size="icon" onClick={() => setInativarId(r.id)}>
                        <Truck className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {busca ? "Nenhum reboque encontrado para esta busca." : "Nenhum reboque cadastrado."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      {/* ── Dialog Criar / Editar ─────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editando ? "Editar Reboque" : "Novo Reboque"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="placa">Placa *</Label>
                <Input
                  id="placa"
                  value={form.placa}
                  onChange={(e) => setForm({ ...form, placa: e.target.value.toUpperCase() })}
                  required
                  placeholder="ABC1D23"
                  maxLength={10}
                  className="font-mono uppercase"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="uf">UF *</Label>
                <Select
                  value={form.uf}
                  onValueChange={(v) => setForm({ ...form, uf: v })}
                >
                  <SelectTrigger id="uf">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {UFS_BR.map((uf) => (
                      <SelectItem key={uf} value={uf}>
                        {uf}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="renavam">RENAVAM</Label>
                <Input
                  id="renavam"
                  value={form.renavam ?? ""}
                  onChange={(e) => setForm({ ...form, renavam: e.target.value })}
                  maxLength={20}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tipoCarroceria">Tipo de Carroceria</Label>
                <Select
                  value={form.tipoCarroceria ?? ""}
                  onValueChange={(v) => setForm({ ...form, tipoCarroceria: v })}
                >
                  <SelectTrigger id="tipoCarroceria">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_CARROCERIA.map((tipo) => (
                      <SelectItem key={tipo} value={tipo} className="capitalize">
                        {tipo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="taraKg">Tara (kg)</Label>
                <Input
                  id="taraKg"
                  type="number"
                  value={form.taraKg ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, taraKg: e.target.value ? Number(e.target.value) : undefined })
                  }
                  min={0}
                  step={0.01}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="capacidadeKg">Capacidade (kg)</Label>
                <Input
                  id="capacidadeKg"
                  type="number"
                  value={form.capacidadeKg ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, capacidadeKg: e.target.value ? Number(e.target.value) : undefined })
                  }
                  min={0}
                  step={0.01}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="capacidadeM3">Capacidade (m³)</Label>
                <Input
                  id="capacidadeM3"
                  type="number"
                  value={form.capacidadeM3 ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, capacidadeM3: e.target.value ? Number(e.target.value) : undefined })
                  }
                  min={0}
                />
              </div>

              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="ativoReb"
                  checked={form.ativo}
                  onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                />
                <Label htmlFor="ativoReb">Ativo</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={criar.isPending || atualizar.isPending}>
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── AlertDialog Inativar ─────────────────────────────────────── */}
      <AlertDialog open={!!inativarId} onOpenChange={() => setInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar reboque</AlertDialogTitle>
            <AlertDialogDescription>
              O reboque será inativado e não aparecerá nos selects de novos MDFes. Histórico de
              documentos já emitidos é preservado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (inativarId) {
                  excluir.mutate(inativarId);
                  setInativarId(null);
                }
              }}
            >
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
